import React, { useState, useRef } from 'react';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import usePopupClose from '../Utils/usePopupClose';

const LLMChallengePopup = ({ 
  show, 
  title = "LLM Challenge Evaluation", 
  eval_id,
  onSubmit,
  onCancel 
}) => {
  const [completeness, setCompleteness] = useState(50);
  const [correctness, setCorrectness] = useState(50);
  const [proficiency, setProficiency] = useState('Middle');
  const [model, setModel] = useState('Claude');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const modalRef = usePopupClose(show, onCancel);
  
  if (!show) return null;
  
  const handleSubmit = async () => {
    // Check for eval_id
    if (!eval_id) {
      onSubmit(false);
      return;
    }

    setIsSubmitting(true);

    try {
      // Make API request
      const response = await fetch(API_ENDPOINTS.SOLVE_CHALLENGE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eval_id,
          completeness,
          correctness,
          proficiency,
          model
        }),
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // Just return success boolean
      onSubmit(true);
    } catch (error) {
      console.error('Error submitting challenge:', error);
      onSubmit(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div ref={modalRef} className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">For testing, this will fill in the practical challenge as if the candidate did it.</p>
        
        {/* Completeness Slider */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Completeness: {completeness}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={completeness}
            onChange={(e) => setCompleteness(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
        
        {/* Correctness Slider */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Correctness: {correctness}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={correctness}
            onChange={(e) => setCorrectness(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
        
        {/* Proficiency Options */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Proficiency
          </label>
          <div className="flex space-x-2">
            {['Junior', 'Middle', 'Senior'].map((level) => (
              <button
                key={level}
                onClick={() => setProficiency(level)}
                className={`flex-1 py-2 px-4 border rounded-md ${
                  proficiency === level
                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
        
        {/* Model Dropdown */}
        <div className="mb-6">
          <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
            Model
          </label>
          <select
            id="model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="GPT">GPT</option>
            <option value="Deepseek">Deepseek</option>
            <option value="Gemini">Gemini</option>
            <option value="Claude">Claude</option>
          </select>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button 
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LLMChallengePopup;
